import { InfraConstants } from 'infra/infraConstants';
import * as R from 'ramda';
import { metrics } from 'system/libs/metrics';
import { CommonModule } from 'utils/CommonModule';
import { FraudLabs } from 'utils/FraudLabs';
import { McsErrors } from 'utils/McsErrors/McsErrors';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { reduxHelper } from 'utils/reduxHelper';
import * as ReduxTypeUtils from 'utils/ReduxTypeUtils/ReduxTypeUtils';

import { ProjectApi } from 'reactApp/api/ProjectApi';
import {
    CurrentUserActions,
    CurrentUserSelectors,
} from 'reactApp/modules/CurrentUserModule';
import * as ProjectTypes from 'reactApp/modules/ProjectModule/ProjectModule.types';
import { Validation } from 'reactComponents/Forms/Validation/Validation';

const {
    Actions: { mergeData, loadData: _loadData },
    Selectors: { getData, getHasError, getIsLoaded, getIsLoading },
} = CommonModule.createCommonModule<ProjectTypes.ProjectData>({
    dataFieldKey: 'project',
    apiMethods: ProjectApi,
    entityName: 'project',
    entityNamePublic: 'проект',
});

const loadData = (pid: string) => {
    // @ts-ignore
    return _loadData(pid);
};

const { addConsumer, removeConsumer } = new ModuleListReloader({
    loadList: loadData,
    updateTimeout: InfraConstants.LONG_UPDATE_INTERVAL,
});

const createProject = (data?: { title?: string }) => async () => {
    try {
        return await ProjectApi.createProject(data);
    } catch (errorResponse) {
        throw handleProjectError(errorResponse, 'create');
    }
};

const handleProjectError = (
    errorResponse,
    action: 'edit' | 'create' | 'createPartner' | 'tfa',
) => {
    const failTexts = {
        edit: 'Не удалось изменить проект.',
        create: 'Не удалось создать проект.',
        createPartner: 'Не удалось отправить заявку.',
        tfa: 'Не удалось сохранить статус двухфакторной авторизации проекта.',
    };
    const failedActionText = failTexts[action];

    const limitError = 'Нельзя создать проект. Превышен лимит.';

    const errorsTexts = McsErrors.getFormMessagesFromResponse(errorResponse, {
        fieldTexts: {
            default: 'Введено некорректное значение',
            title: {
                conflict: 'Имя уже занято',
                argument_invalid: Validation.PROJECT_NAME_VALIDATION_ERROR_TEXT,
            },
            limit: {
                default: limitError,
            },
        },
        commonTexts: {
            default: `${failedActionText} ${McsErrors.TRY_LATER_HTML}`,
            project_limit: limitError,
        },
    });

    if (errorsTexts.fieldMessages.limit) {
        errorsTexts.commonMessages.push(errorsTexts.fieldMessages.limit);
        delete errorsTexts.fieldMessages.limit;
    }

    return errorsTexts;
};

const createProjectAndSwitchTo = (data?: { title?: string }) => async (
    dispatch,
) => {
    const project = await dispatch(createProject(data));
    dispatch(CurrentUserActions.setSelectedPid(project.pid));
    window.location.href = '/app';
};

const editProject = (
    pid: string,
    data: ProjectTypes.ProjectEditableData,
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    try {
        await ProjectApi.editData(pid, data);
        await dispatch(CurrentUserActions.loadUser());
        dispatch(mergeData(data));
    } catch (errorResponse) {
        throw handleProjectError(errorResponse, 'edit');
    }
};

const setDebitNotification = (
    pid: string,
    cmd: { enableDebitNotification: boolean; limit2notify: string },
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    try {
        if (!cmd.enableDebitNotification) {
            const project = await ProjectApi.disableDebitNotif(pid);
            return dispatch(mergeData(project));
        }

        const project = await ProjectApi.enableDebitNotif(
            pid,
            +cmd.limit2notify,
        );
        dispatch(mergeData(project));
    } catch (errorResponse) {
        return errorResponse;
    }
};

const getIsTfaEnabled: ReduxTypeUtils.Selector<boolean | undefined> = R.pipe(
    getData,
    R.pathOr(false, ['attr', 'tfa', 'enabled']),
);

const setProjectTfaStatus = (
    pid: string,
    newStatus: boolean,
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch, getState) => {
    try {
        const method = newStatus ? 'enableTfa' : 'disableTfa';
        await ProjectApi[method](pid);

        const oldAttr = getAttributes(getState());
        dispatch(
            mergeData({ attr: { ...oldAttr, tfa: { enabled: newStatus } } }),
        );
    } catch (errorResponse) {
        throw handleProjectError(errorResponse, 'tfa');
    }
};

const createPartnerProject = (
    params: ProjectTypes.PartnerProjectCreatingParams,
) => async (dispatch) => {
    let project: ProjectTypes.ProjectData;
    try {
        project = await ProjectApi.createPartnerProject(params);
    } catch (errorResponse) {
        throw handleProjectError(errorResponse, 'createPartner');
    }

    await dispatch(CurrentUserActions.loadUser());

    dispatch(mergeData(project));
    dispatch(CurrentUserActions.setSelectedPid(project.pid));

    metrics.send({
        category: 'partnership',
        action: 'RegistrationStart',
    });

    return project;
};

const fraudCheck = () => async (dispatch, getState) => {
    const pid = CurrentUserSelectors.getPid(getState());
    await FraudLabs.loadAgent();
    await ProjectApi.fraudCheck(pid);
    await dispatch(loadData(pid));
};

export const ProjectActions = {
    loadData,
    addConsumer,
    removeConsumer,

    createProject,
    createProjectAndSwitchTo,
    createPartnerProject,
    editProject,
    setDebitNotification,
    setProjectTfaStatus,

    fraudCheck,
};

const getProjectTitle = R.pipe(getData, (data) => data.title);

const getIsPartnerApproved: ReduxTypeUtils.Selector<boolean> = R.pipe(
    getData,
    R.pathOr(false, ['attr', 'partner_approved']),
);

const getAttributes = R.pipe(getData, R.prop('attr'));

const getIsTrusted = R.pipe(getAttributes, (attrs) => attrs.trusted);

const getIsTrustedPartner: ReduxTypeUtils.Selector<boolean> = R.allPass([
    getIsTrusted,
    getIsPartnerApproved,
]);

const getFraudStatus: ReduxTypeUtils.Selector<string | undefined> = R.pipe(
    getData,
    R.path(['verified', 'fraud', 'status']),
);

const getIsUnverifiedProject = R.pipe(getFraudStatus, R.equals('NEW'));

const getIsSuspiciousProject = R.pipe(
    getFraudStatus,
    R.anyPass([R.equals('REVIEW'), R.equals('REJECT')]),
);

const getIsProjectEnabled = R.pipe(getData, R.prop('enabled'));

const getIsProjectDisabled = R.pipe(getIsProjectEnabled, R.not);

const getInfoOfPartner = R.pipe(getData, (data) => data && data.partner);

const getIsClientOfPartner = R.pipe(getInfoOfPartner, Boolean);

const getCanPay = R.pipe(getInfoOfPartner, R.not);

function getOwnerEmail(state) {
    const data = getData(state);
    return data && data.owner.email;
}

const getIsInfraAutoEnabledForNewClients: ReduxTypeUtils.Selector<boolean> = R.pipe(
    getAttributes,
    R.pathOr(false, ['start_services', 'infra']),
);

export const ProjectSelectors = {
    getData,

    getOwnerEmail,
    getHasError,
    getIsLoaded,
    getIsLoading,

    getIsTrusted,
    getIsTrustedPartner,

    getIsProjectEnabled,
    getIsProjectDisabled,

    getProjectTitle,

    getIsPartnerApproved,

    getIsUnverifiedProject,
    getIsSuspiciousProject,

    getInfoOfPartner,
    getIsClientOfPartner,
    getCanPay,

    getIsInfraAutoEnabledForNewClients,

    getIsTfaEnabled,
};

export const BoundProjectActions = reduxHelper.bindActions<
    typeof ProjectActions
>(ProjectActions);

export const BoundProjectSelectors = reduxHelper.bindSelectors<
    typeof ProjectSelectors
>(ProjectSelectors);
